import React from 'react';
import { toast } from 'sonner';
import { UtensilsCrossed } from 'lucide-react';
import { useGet, apiGet, apiPut } from '../../hooks/useApi.js';

interface TechRow {
  id: string;
  name: string;
  active: boolean;
  workProfile?: { lunchStart: string; lunchEnd: string; timezone: string } | null;
}

const TIME_OPTIONS = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      out.push(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
    }
  }
  return out;
})();

function fmtTime(hhmm: string): string {
  const parts = hhmm.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + ':' + String(m).padStart(2, '0') + ' ' + ampm;
}

export function AdminLunchPage() {
  const { data, loading, refetch } = useGet<{ items: TechRow[] }>('/api/v1/technicians?includeInactive=true');
  const [rows, setRows] = React.useState<Record<string, { lunchStart: string; lunchEnd: string }>>({});
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (data && !initialized.current) {
      const next: Record<string, { lunchStart: string; lunchEnd: string }> = {};
      for (const t of data.items ?? []) {
        next[t.id] = {
          lunchStart: t.workProfile?.lunchStart ?? '12:00',
          lunchEnd: t.workProfile?.lunchEnd ?? '13:00',
        };
      }
      setRows(next);
      initialized.current = true;
    }
  }, [data]);

  async function loadFullProfile(techId: string) {
    // The list view may not include the profile; fetch it, then prime the row.
    try {
      const p = await apiGet<{ lunchStart: string; lunchEnd: string }>('/api/v1/technicians/' + techId + '/work-profile');
      setRows((prev) => ({ ...prev, [techId]: { lunchStart: p.lunchStart ?? '12:00', lunchEnd: p.lunchEnd ?? '13:00' } }));
    } catch { /* keep defaults */ }
  }

  React.useEffect(() => {
    if (data && initialized.current) {
      for (const t of data.items ?? []) if (!rows[t.id]) void loadFullProfile(t.id);
    }
  }, [data]);

  async function save(tech: TechRow) {
    const row = rows[tech.id];
    if (!row || row.lunchStart >= row.lunchEnd) {
      toast.error('Lunch start must be before lunch end for ' + tech.name);
      return;
    }
    setSavingId(tech.id);
    try {
      // GET current full profile (to preserve capacity/admin/training), then PUT with the new lunch window.
      let full: Record<string, unknown>;
      try {
        full = await apiGet<Record<string, unknown>>('/api/v1/technicians/' + tech.id + '/work-profile');
      } catch {
        full = {};
      }
      await apiPut('/api/v1/technicians/' + tech.id + '/work-profile', {
        timezone: full.timezone ?? 'America/Chicago',
        dailyCapacityMinutes: full.dailyCapacityMinutes ?? 480,
        billableTargetMinutes: full.billableTargetMinutes ?? null,
        lunchStart: row.lunchStart,
        lunchEnd: row.lunchEnd,
        adminTimeMinutes: full.adminTimeMinutes ?? 30,
        adminStartTime: full.adminStartTime ?? '08:00',
        trainingTimeMinutes: full.trainingTimeMinutes ?? 60,
        trainingRecurrence: full.trainingRecurrence ?? 'weekly',
        trainingDayOfWeek: full.trainingDayOfWeek ?? 2,
        trainingStartTime: full.trainingStartTime ?? '14:00',
      });
      toast.success(tech.name + ': lunch set to ' + fmtTime(row.lunchStart) + '–' + fmtTime(row.lunchEnd) + ' — calendar updates within the hour');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save ' + tech.name);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <UtensilsCrossed className="h-8 w-8 text-brand-600" />
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Lunch Schedules</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))]">Default lunch window per technician. Techs can override their own in their Settings page; changes re-book calendar blocks within the hour.</p>
        </div>
      </div>

      <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6">
        {loading ? (
          <p className="text-sm text-[hsl(var(--content-subtle))]">Loading technicians…</p>
        ) : (data?.items ?? []).length === 0 ? (
          <p className="text-sm text-[hsl(var(--content-subtle))]">No technicians found.</p>
        ) : (
          <div className="divide-y divide-[hsl(var(--border-subtle))]">
            {(data?.items ?? []).map((t) => {
              const row = rows[t.id] ?? { lunchStart: '12:00', lunchEnd: '13:00' };
              return (
                <div key={t.id} className="flex items-center gap-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{t.name}{!t.active ? ' (inactive)' : ''}</p>
                    <p className="text-xs text-[hsl(var(--content-subtle))]">{t.workProfile?.timezone ?? 'America/Chicago'}</p>
                  </div>
                  <select value={row.lunchStart} disabled={savingId === t.id}
                    onChange={(e) => setRows((p) => ({ ...p, [t.id]: { ...row, lunchStart: e.target.value } }))}
                    className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-sm text-[hsl(var(--content))] outline-none">
                    {TIME_OPTIONS.map((tm) => <option key={tm} value={tm}>{fmtTime(tm)}</option>)}
                  </select>
                  <span className="text-sm text-[hsl(var(--content-subtle))]">to</span>
                  <select value={row.lunchEnd} disabled={savingId === t.id}
                    onChange={(e) => setRows((p) => ({ ...p, [t.id]: { ...row, lunchEnd: e.target.value } }))}
                    className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-sm text-[hsl(var(--content))] outline-none">
                    {TIME_OPTIONS.map((tm) => <option key={tm} value={tm}>{fmtTime(tm)}</option>)}
                  </select>
                  <button type="button" onClick={() => save(t)} disabled={savingId === t.id || row.lunchStart >= row.lunchEnd}
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {savingId === t.id ? 'Saving…' : 'Save'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
